import React, { FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment-timezone';
import DateTimeHover from '../date_time_component/DateTime';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { toolCheckoutsRestCrudThunksForSupplier } from '../../thunks/tool_checkouts_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { EmptyState } from '../empty_state/EmptyState';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';

const MY_PICKUPS_HISTORY_TARGET_COLLECTION = 'myPickupsHistoryIndex';

const ToolsMyPickupsHistoryPage: FC<any> = ({ toolCheckouts }): React.ReactElement => {
	const initialFilters = useMemo(() => ({ pickedUpForMe: true, status: 'closed' }), []);

	const columns = useMemo(
		() => [
			{
				title: 'Date',
				dataIndex: 'pickedUpAt',
				render: (text, record) => {
					const tz = nullSafeGet('tool.stockLocation.timeZoneId', record);
					const pickupDate = tz ? moment.tz(record.pickedUpAt, tz) : moment(record.pickedUpAt);
					const dropoffDate = record.droppedOffAt
						? tz
							? moment.tz(record.droppedOffAt, tz)
							: moment(record.droppedOffAt)
						: null;
					const sameDay = dropoffDate && pickupDate.isSame(dropoffDate, 'day');

					if (!dropoffDate) {
						return (
							<div>
								<span className="bodymedium">
									<DateTimeHover
										timestamp={record.pickedUpAt}
										timeZone={tz}
										dateFormat="D MMM"
										showDate
									/>
								</span>
								<span className="bodyregular ml-2 text-character-secondary">
									<DateTimeHover
										timestamp={record.pickedUpAt}
										timeZone={tz}
										dateFormat="ha z"
										showDate
									/>
								</span>
							</div>
						);
					}

					if (sameDay) {
						return (
							<div>
								<span className="bodymedium">
									<DateTimeHover
										timestamp={record.pickedUpAt}
										timeZone={tz}
										dateFormat="D MMM"
										showDate
									/>
								</span>
								<span className="bodyregular ml-2 text-character-secondary">
									<DateTimeHover
										timestamp={record.pickedUpAt}
										timeZone={tz}
										dateFormat="ha"
										showDate
									/>
									{' - '}
									<DateTimeHover
										timestamp={record.droppedOffAt}
										timeZone={tz}
										dateFormat="ha z"
										showDate
									/>
								</span>
							</div>
						);
					}

					return (
						<div>
							<span className="bodymedium">
								<DateTimeHover
									timestamp={record.pickedUpAt}
									timeZone={tz}
									dateFormat="D MMM"
									showDate
								/>
							</span>
							<span className="bodyregular ml-2 text-character-secondary">
								<DateTimeHover
									timestamp={record.pickedUpAt}
									timeZone={tz}
									dateFormat="ha"
									showDate
								/>
							</span>
							<span className="bodyregular ml-1 text-character-secondary">-</span>
							<span className="bodymedium ml-1">
								<DateTimeHover
									timestamp={record.droppedOffAt}
									timeZone={tz}
									dateFormat="D MMM"
									showDate
								/>
							</span>
							<span className="bodyregular ml-2 text-character-secondary">
								<DateTimeHover
									timestamp={record.droppedOffAt}
									timeZone={tz}
									dateFormat="ha z"
									showDate
								/>
							</span>
						</div>
					);
				},
			},
			{
				title: 'Tool',
				dataIndex: 'tool',
				render: (text, record) => (
					<span className="bodymedium">{nullSafeGet('tool.name', record) || '--'}</span>
				),
			},
			{
				title: 'Location',
				dataIndex: 'stockLocation',
				render: (text, record) => (
					<span className="bodyregular">
						{nullSafeGetOrElse('tool.stockLocation.name', record, '--')}
					</span>
				),
			},
			{
				title: 'Picked Up By',
				dataIndex: 'pickedUpBy',
				render: (text, record) => {
					const pickedUpBy = nullSafeGet('pickedUpBy', record);
					const pickedUpFor = nullSafeGet('pickedUpFor', record);
					if (pickedUpBy === pickedUpFor) {
						return <span className="bodyregular">Self</span>;
					}
					const pickedUpByName =
						getContactName(nullSafeGet('pickedUpByContact', record)).trim() || '--';
					const droppedOffByName =
						getContactName(nullSafeGet('droppedOffByContact', record)).trim() || 'Self';
					return (
						<span className="bodyregular">
							Pickup: {pickedUpByName} / Dropoff: {droppedOffByName}
						</span>
					);
				},
			},
			{
				title: 'Work Orders',
				dataIndex: 'workOrders',
				render: (text, record) => {
					const workOrders = nullSafeGetOrElse('workOrders', record, []);
					if (!workOrders || workOrders.length === 0) {
						return <span className="bodyregular">--</span>;
					}
					return (
						<div className="flex flex-row flex-wrap gap-x-3 gap-y-1">
							{workOrders.map((workOrder) => (
								<HyperLink
									key={workOrder.id}
									entityType={ENTITY_TYPE.WORK_ORDER}
									entityId={workOrder.id}
									text={`WO #${workOrder.id}`}
								/>
							))}
						</div>
					);
				},
			},
		],
		[]
	);

	return (
		<ReduxDataTable
			entityCollectionName="tool_checkouts"
			targetCollectionName={MY_PICKUPS_HISTORY_TARGET_COLLECTION}
			fetchData={toolCheckoutsRestCrudThunksForSupplier}
			stateSlice={toolCheckouts}
			tableColumns={columns}
			filterConfig={[]}
			keyAccessor={(el) => el.id}
			showHeader={false}
			hideFilters
			hideSearch
			initialFilters={initialFilters}
			emptyState={
				<EmptyState
					headline="No pickup history"
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Your completed tool pickups will appear here.
							</div>
						</div>
					}
				/>
			}
		/>
	);
};

const mapStateToProps = (state) => ({
	toolCheckouts: state.tool_checkouts,
});

const mapDispatchToProps = (dispatch) => ({});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ToolsMyPickupsHistoryPage)
);
